import { handleAxiosError } from '@/api/helpers';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants/routes';
import { useConfirmDomainVerification } from '@/hooks/query/account-domains.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppSpinner } from '@/ui/app-spinner';
import { FlexContainer } from '@/ui/styled-ui';
import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

export const AccountDomainVerificationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { confirmVerification } = useConfirmDomainVerification();
  const hasCalledRef = useRef(false);
  const currentAccountRef = useRef(currentAccount);
  currentAccountRef.current = currentAccount;

  const navigateToEmailsAndDelivery = useCallback(() => {
    const account = currentAccountRef.current;
    account?.isSystem
      ? navigate(
          RedirectPaths[RedirectPathsEnum.ACCOUNTS_EMAILS_AND_DELIVERY](
            account?.id as string,
          ),
          { replace: true },
        )
      : navigate(
          RedirectPaths[RedirectPathsEnum.ACCOUNT_EMAILS_AND_DELIVERY](),
          { replace: true },
        );
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigateToEmailsAndDelivery();
      return;
    }

    if (hasCalledRef.current) return;
    hasCalledRef.current = true;

    confirmVerification(token)
      .then(() => {
        toast?.success(
          t('toast.success'),
          t('account.domains.verification.success'),
        );
        navigateToEmailsAndDelivery();
      })
      .catch((e) => {
        handleAxiosError(e as Error | AxiosError, toast);
        navigateToEmailsAndDelivery();
      });
  }, [token, confirmVerification, navigateToEmailsAndDelivery, toast, t]);

  return (
    <FlexContainer
      direction="column"
      align="center"
      justify="center"
      height="100vh"
      gap="1.5rem"
    >
      <AppSpinner width={60} height={60} />
      <h2>{t('account.domains.verification.verifying')}</h2>
    </FlexContainer>
  );
};
